import events from "_src/engine/events";
import {vec3, quat, mat4, vec2, vec4} from "_src/libs/gl-matrix";
import CameraControl from "_src/client/controls/camera_control";
import appState from "_src/app_state";
import {screenUtils} from "_src/client/utils";
import {photoOrientation} from "_src/constants";
import {easingType} from "_src/engine/constants";

const PAN_SPEED_MULTIPLIER = 1/10;

const SCREEN_MARGIN = 50; //virtual px
const PANEL_HEIGHT = 60; //virtual px

export default class PhotoControl extends CameraControl {

  constructor(object) {
    super(object);
    this._animation.type = easingType.OUT_EXPO;

    this._resetState();
  }

  _resetState() {
    this._fovRange = [0, 0];
    this._cameraRect = null;
    this._screenRect = null;
    this._defaultShift = [0, 0];
    this._shiftRange = [0, 0, 0, 0];
    this._savedFov = 0;
    this._savedRotationStart = null;
  }

  moveToPhoto(model, orientation, animationTime, instantRectangle, onFinish) {
    const camera = this.object;
    this._savedFov = camera.fov;

    const rotation = quat.clone(model.quat);
    quat.rotateX(rotation, rotation, Math.PI);
    const orientationRotation = _getOrientationRotation(orientation);
    quat.multiply(rotation, rotation, orientationRotation);

    const startPosition = vec3.clone(camera.trans);
    const finalPosition = vec3.clone(model.trans);
    const currentPosition = vec3.create();

    const startRotation = quat.clone(camera.quat);
    const finalRotation = rotation;
    const currentRotation = quat.create();

    const startFov = camera.fov;
    let finalFov = startFov;
    let currentFov;

    const startShift = vec2.clone(camera.projectionShift);
    const finalShift = this._defaultShift;
    const currentShift = vec2.create();

    const updateCameraState = (t) => {
      vec3.lerp(currentPosition, startPosition, finalPosition, t);
      quat.slerp(currentRotation, startRotation, finalRotation, t);
      currentFov = startFov + (finalFov - startFov) * t;
      vec2.lerp(currentShift, startShift, finalShift, t);

      camera.setRotation(...currentRotation);
      camera.setTranslation(...currentPosition);
      camera.setFovY(currentFov);
      camera.projectionShift[0] = currentShift[0];
      camera.projectionShift[1] = currentShift[1];
      camera.updateMatrix();
      camera.updateProjectMatrix();

      events.emit("camera_move", camera);
      events.emit("frame_requested");
    }

    this._animation.setOnUpdate((value) => {
      updateCameraState(value);
    });

    this._animation.setOnFinish(() => {
      updateCameraState(1);
      if (!instantRectangle) _emitRectangle(this._screenRect, camera);
      onFinish();
    });

    this._animation.start(0, 1, animationTime);

    //set camera to final position to estimate fov and rects
    updateCameraState(1);
    this._cameraRect = _cameraRectByModel(model, camera);
    const maxFov = _fovByCamera(camera, this._cameraRect);
    finalFov = maxFov;
    this._fovRange = _getFovRange(maxFov);
    this._screenRect = _screenRectByFov(maxFov, camera, this._cameraRect, this._defaultShift);
    this._shiftRange = [this._defaultShift[0], this._defaultShift[1], this._defaultShift[0], this._defaultShift[1]];
    if (instantRectangle) _emitRectangle(this._screenRect, camera);

    return this._screenRect;
  }

  moveOutOfPhoto(animationTime, onFinish) {
    const camera = this.object;

    const startProjectionShift = vec2.clone(camera.projectionShift);
    const finalProjectionShift = vec2.fromValues(0,0);
    const currentProjectionShift = vec2.create();

    const startFov = camera.fov;
    const finalFov = this._savedFov;
    const fovDelta = finalFov - startFov;
    let currentFov;

    const startPosition = vec3.clone(camera.trans);
    const finalPosition = _cameraPosByFov(camera, this._savedFov, this._screenRect, this._cameraRect);
    const currentPosition = vec3.create();

    const updateCameraState = (t) => {
      vec3.lerp(currentPosition, startPosition, finalPosition, t);
      vec2.lerp(currentProjectionShift, startProjectionShift, finalProjectionShift, t);
      currentFov = startFov + fovDelta * t;

      camera.setTranslation(...currentPosition);
      camera.setFovY(currentFov);
      camera.projectionShift = currentProjectionShift;
      camera.updateMatrix();
      camera.updateProjectMatrix();

      events.emit("camera_move", camera);
      events.emit("frame_requested");
    }

    this._animation.setOnUpdate((value) => {
      updateCameraState(value);
    });

    this._animation.setOnFinish(() => {
      updateCameraState(1);
      this._resetState();

      onFinish();
    });

    this._animation.start(0, 1, animationTime);

    events.emit("photoViewRectangleExternal", null);
  }

  zoom(delta) {
    if (this._animation.checkBusy() || !this.enabled) return;

    const camera = this.object;
    const newFov = Math.max(Math.min(camera.fov + delta, this._fovRange[1]), this._fovRange[0]);
    camera.setFovY(newFov);
    camera.updateProjectMatrix();

    const zoomedScreenRect = _screenRectByFov(camera.fov, camera, this._cameraRect, this._defaultShift);
    this._shiftRange = [
      this._defaultShift[0] - (this._screenRect[0] - zoomedScreenRect[0]),
      this._defaultShift[1] - (this._screenRect[1] - zoomedScreenRect[1]),
      this._defaultShift[0] + (this._screenRect[0] - zoomedScreenRect[0]),
      this._defaultShift[1] + (this._screenRect[1] - zoomedScreenRect[1])
    ];

    _updateCameraShift(camera, this._shiftRange);
  }

  pan(dX, dY) {
    if (this._animation.checkBusy() || !this.enabled) return;

    const camera = this.object;

    camera.projectionShift[0] -= dX * PAN_SPEED_MULTIPLIER;
    camera.projectionShift[1] += dY * PAN_SPEED_MULTIPLIER;

    _updateCameraShift(camera, this._shiftRange);
  }

  startRotation(x, y) {
    this._savedRotationStart = [x, y];
  }

  rotate() {
    if (this._animation.checkBusy() || !this.enabled) return;

    events.emit("exitPhotoMode", this._savedRotationStart);
  }

  endRotation() {
    this._savedRotationStart = null;
  }

  resize() {
    const camera = this.object;
    const maxFov = _fovByCamera(camera, this._cameraRect);
    this._fovRange = _getFovRange(maxFov);
    this._screenRect = _screenRectByFov(maxFov, camera, this._cameraRect, this._defaultShift);
    this._shiftRange = [
      this._defaultShift[0] - (this._screenRect[0] - this._screenRect[0]),
      this._defaultShift[1] - (this._screenRect[1] - this._screenRect[1]),
      this._defaultShift[0] + (this._screenRect[0] - this._screenRect[0]),
      this._defaultShift[1] + (this._screenRect[1] - this._screenRect[1])
    ];
    _updateCameraShift(camera, this._shiftRange);
    camera.setFovY(maxFov);
    camera.updateProjectMatrix();

    _emitRectangle(this._screenRect, camera);
    return this._screenRect;
  }
}

function _cameraRectByModel(model, camera) {
  const geometry = model.getChildren()[0].chunks[0].geometry;
  const posBuffer = geometry.getItemBuffer("a_position");

  const modelTransform = model.getChildren()[0].modelMatrix;
  let cameraRect = [Infinity, Infinity, -Infinity, -Infinity, Infinity];
  for (let i=0; i<posBuffer.length/3; i++) {
    const pos = vec3.fromValues(posBuffer[i*3 + 0], posBuffer[i*3 + 1], posBuffer[i*3 + 2]);
    const worldPos = vec3.transformMat4(vec3.create(), pos, modelTransform);
    const camSpacePos = vec3.transformMat4(vec3.create(), worldPos, camera.viewMat);
    cameraRect = [Math.min(camSpacePos[0], cameraRect[0]), Math.min(camSpacePos[1], cameraRect[1]),
      Math.max(camSpacePos[0], cameraRect[2]), Math.max(camSpacePos[1], cameraRect[3]), camSpacePos[2]];
  }

  return cameraRect;
}

function _cameraPosByFov(camera, fov, screenRect, cameraRect) {
  const ndcWidth = screenRect[2];
  const cameraSpaceHalfWidth = cameraRect[2] - cameraRect[0];
  const scenePixelWidth = camera.viewport[2];
  const scenePixelHeight = camera.viewport[3];
  const sceneAspect = scenePixelWidth / scenePixelHeight;
  const offset = -((1 / Math.tan(fov / 2)) * cameraSpaceHalfWidth / sceneAspect / ndcWidth);
  const currentOffset = cameraRect[4];

  const viewInvMat = mat4.invert(mat4.create(), camera.viewMat);
  const camSpaceShift = vec4.fromValues(0, 0, currentOffset - offset, 0);
  const worldSpaceShift = vec4.transformMat4(vec4.create(), camSpaceShift, viewInvMat);

  const worldPos = vec3.add(vec3.create(), camera.trans, worldSpaceShift);

  return worldPos;
}

function _fovByCamera(camera, cameraRect) {
  const photoWidth = cameraRect[2] - cameraRect[0];
  const photoHeight = cameraRect[3] - cameraRect[1];
  const photoAspect = photoWidth / photoHeight;

  const scenePixelWidth = camera.viewport[2];
  const scenePixelHeight = camera.viewport[3];
  const sceneAspect = scenePixelWidth / scenePixelHeight;

  const fittingArea = appState.getStateValue("photoFitArea");
  const fittingAreaShift = [
    scenePixelWidth - fittingArea[2],
    scenePixelHeight - fittingArea[3],
  ];

  const maxPhotoWidth = scenePixelWidth - 2 * SCREEN_MARGIN * screenUtils.getPixelRatio() - fittingAreaShift[0];
  const maxPhotoHeight = scenePixelHeight - 2 * SCREEN_MARGIN * screenUtils.getPixelRatio() - PANEL_HEIGHT * screenUtils.getPixelRatio() - fittingAreaShift[1];
  const maxPhotoAspect = maxPhotoWidth / maxPhotoHeight;

  let f;
  if (maxPhotoAspect > photoAspect) {
    const ndcHeight = maxPhotoHeight / scenePixelHeight;
    const cameraSpaceHalfHeight = (cameraRect[3] - cameraRect[1]) / 2;

    f = ndcHeight * (-cameraRect[4]) / cameraSpaceHalfHeight;
  } else {
    const ndcWidth = maxPhotoWidth / scenePixelWidth;
    const cameraSpaceHalfWidth = (cameraRect[2] - cameraRect[0]) / 2;

    f = ndcWidth * (-cameraRect[4]) * sceneAspect / cameraSpaceHalfWidth;
  }
  const fov = Math.atan(1 / f) * 2;

  return fov;
}

function _screenRectByFov(fov, camera, cameraRect, defaultShift) {
  let screenProjectedRect = [Infinity, Infinity, -Infinity, -Infinity];
  const projMatrix = mat4.perspective(mat4.create(), fov, camera.aspect, camera.near, camera.far);
  const camSpacePositions = [
      [cameraRect[0], cameraRect[1], cameraRect[4]],
      [cameraRect[0], cameraRect[3], cameraRect[4]],
      [cameraRect[2], cameraRect[3], cameraRect[4]],
      [cameraRect[2], cameraRect[1], cameraRect[4]],
  ];
  for (const pos of camSpacePositions) {
    const screenProjectedPos = vec3.transformMat4(vec3.create(), pos, projMatrix);
    screenProjectedRect = [Math.min(screenProjectedPos[0], screenProjectedRect[0]), Math.min(screenProjectedPos[1], screenProjectedRect[1]),
      Math.max(screenProjectedPos[0], screenProjectedRect[2]), Math.max(screenProjectedPos[1], screenProjectedRect[3])];
  }
  const panelNdcShift = PANEL_HEIGHT * screenUtils.getPixelRatio() / camera.viewport[3];
  const fittingArea = appState.getStateValue("photoFitArea");
  const fittingAreaCenter = [fittingArea[0] + fittingArea[2] / 2, fittingArea[1] + fittingArea[3] / 2];
  const fittingAreaNdcShift = [(fittingAreaCenter[0] / camera.viewport[2] - 0.5) * 2, (fittingAreaCenter[1] / camera.viewport[3] - 0.5) * 2];

  const shiftedRect = [
    screenProjectedRect[0] + fittingAreaNdcShift[0],
    screenProjectedRect[1] + panelNdcShift - fittingAreaNdcShift[1],
    screenProjectedRect[2] + fittingAreaNdcShift[0],
    screenProjectedRect[3] + panelNdcShift - fittingAreaNdcShift[1],
  ];
  defaultShift[0] = screenProjectedRect[0] - shiftedRect[0];
  defaultShift[1] = screenProjectedRect[1] - shiftedRect[1];

  return shiftedRect;
}

function _updateCameraShift(camera, shiftRange) {
  const newShiftX = Math.max(Math.min(camera.projectionShift[0], shiftRange[2]), shiftRange[0]);
  const newShiftY = Math.max(Math.min(camera.projectionShift[1], shiftRange[3]), shiftRange[1]);
  camera.projectionShift[0] = newShiftX;
  camera.projectionShift[1] = newShiftY;
  camera.updateProjectMatrix();
  events.emit("camera_move", camera);
}

function _emitRectangle(ndcRect, camera) {
  const leftTop = screenUtils.fromScreen(camera.viewport, camera.viewport, ndcRect[0], -ndcRect[1]);
  const rightBottom = screenUtils.fromScreen(camera.viewport, camera.viewport, ndcRect[2], -ndcRect[3]);
  const internalRectangle = [Math.ceil(leftTop.x), Math.ceil(camera.viewport[3] - leftTop.y), Math.floor(rightBottom.x - leftTop.x), Math.floor(leftTop.y - rightBottom.y)];

  const externalRectangle = [Math.floor(leftTop.x) / screenUtils.getPixelRatio(), Math.floor(rightBottom.y) / screenUtils.getPixelRatio(),
    Math.ceil(rightBottom.x - leftTop.x) / screenUtils.getPixelRatio(), Math.ceil(leftTop.y - rightBottom.y + PANEL_HEIGHT) / screenUtils.getPixelRatio()];

  events.emit("photoViewRectangleExternal", externalRectangle);
  appState.setStateValue("photoViewRectangleInternal", internalRectangle);
}

function _getOrientationRotation(orientation) {
  const defaultCorrection = quat.identity(quat.create());

  switch (orientation) {
    case photoOrientation.DEFAULT:
    case photoOrientation.MIRROR_HORIZONTAL:
      return defaultCorrection;
    case photoOrientation.MIRROR_VERTICAL:
    case photoOrientation.ROTATION180: {
      return quat.rotateZ(quat.create(), defaultCorrection, Math.PI);
    }
    case photoOrientation.ROTATION270_MIRROR_HORIZONTAL:
    case photoOrientation.ROTATION270: {
      return quat.rotateZ(quat.create(), defaultCorrection, Math.PI * 1.5);
    }
    case photoOrientation.ROTATION90_MIRROR_HORIZONTAL:
    case photoOrientation.ROTATION90: {
      return quat.rotateZ(quat.create(), defaultCorrection, Math.PI * 0.5);
    }
  }
}

function _getFovRange(maxFov) {
  return [maxFov - 0.75, maxFov];
}
